
import {
    Container,
    Image,
    Segment,
    Dropdown,
    Header,
    Table,
    Button,
    Icon,
} from 'semantic-ui-react';

import { useState } from 'react';

import moment from 'moment';

import { Link } from "react-router-dom";

import { ResponsiveContainer } from '../ResponsiveContainer';

import { useMutation, useQuery, gql } from '@apollo/client';

import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../queries/tags';

const SET_AS_CALLED = gql`
mutation SetAsCalled($alertId: Int) {
  setAsCalled(alertId: $alertId) {
    id
  }
}`

const KICK_FROM_CLASS = gql`
    mutation KickStudentFromClass($courseId: Int, $userId: Int) {
  kickStudentFromClass(courseId: $courseId, userId: $userId) {
    status
  }
}`

const CLEAR_ATTENDANCE_ALERTS = gql`
    mutation ClearAttendanceAlerts($filters: String) {
  clearAttendanceAlerts(filters: $filters)
}`

const GET_ATTENDANCE_ALERTS = gql`
query AttendanceAlerts($filters: String) {
  attendanceAlerts(filters: $filters) {
    id
    course {
        id
        name
        SKU
        teachers {
            id
            name
            photoUrl
        }
    }
    absentCount
    user {
        id
        identifier
        name
        phone
    }
    assignedUserId
    createdAt
    resetTime
    shouldShow
    updatedAt
    calledAt
    caller {
        id
        name
    }
  }
}`

const decideBackgroundColor = (count: number) => {
    switch (count) {
        case 1:
            return 'none'; // light yellow
        case 2:
            return 'yellow'; // medium yellow
        case 3:
            return 'red'; // light red
        default:
            return 'aqua'; // darker red for 4 or more
    }
}

const STORAGE_KEY = 'attendanceDashboard_tagIds';
const TEACHER_STORAGE_KEY = 'attendanceDashboard_teacherId';
const IDENTIFIER_PREFIX_STORAGE_KEY = 'attendanceDashboard_identifierPrefix';

const Attendance = () => {
    const [tagIds, setTagIds] = useState<string[]>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });
    // Teacher filter — applied client-side, independent of the (server-side) tag filter.
    const [teacherId, setTeacherId] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(TEACHER_STORAGE_KEY);
            return stored ? JSON.parse(stored) : '';
        } catch {
            return '';
        }
    });
    // Identifier prefix filter — applied client-side (e.g. KP, SG, RSB)
    const [identifierPrefix, setIdentifierPrefix] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(IDENTIFIER_PREFIX_STORAGE_KEY);
            return stored ? JSON.parse(stored) : '';
        } catch {
            return '';
        }
    });
    const { loading, error, data, refetch } = useQuery(GET_ATTENDANCE_ALERTS, {
        variables: { filters: tagIds.length > 0 ? tagIds.join(',') : '' },
        notifyOnNetworkStatusChange: true,
    });
    const [missedClassesSort, setMissedClassesSort] = useState<'ascending' | 'descending' | null>(null);
    const { loading: tagsLoading, error: tagsError, data: tagsData } = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        }
    });

    const [setCalled] = useMutation(SET_AS_CALLED);
    const [kickFromClass] = useMutation(KICK_FROM_CLASS);
    const [clearAlerts, { loading: clearing }] = useMutation(CLEAR_ATTENDANCE_ALERTS);

    if (error && data.attendanceAlerts && data.attendanceAlerts.length === 0) {
        console.error(error);
        return <p>Error loading attendance alerts.</p>;
    }

    const teacherItem = (teacher: any) => {
        return (
            <>
                <Image avatar src={teacher.photoUrl} size='tiny' style={{ width: '2em', height: '2em', objectFit: 'cover' }} />
                <Link to={`/people/${teacher.id}`}>
                    {teacher.name}
                </Link>
                <br />
            </>
        )
    }

    const alerts = (data?.attendanceAlerts ?? []).filter((a: any) => a?.user != null);

    // Teacher options are the set of teachers present in the current results.
    type TeacherOption = { key: number; text: string; value: string };
    const teacherOptions: TeacherOption[] = Object.values(
        alerts
            .flatMap((a: any) => a?.course?.teachers ?? [])
            .reduce((acc: Record<string, TeacherOption>, o: any) => {
                acc[o.id] = { key: o.id, text: o.name, value: String(o.id) };
                return acc;
            }, {} as Record<string, TeacherOption>)
    );

    // Identifier prefix options (e.g. KP, SG, RSB) derived from current results.
    const prefixOptions = Array.from(
        new Set(
            alerts
                .map((a: any) => a?.user?.identifier?.match(/^([A-Z]+)/)?.[1])
                .filter(Boolean) as string[]
        )
    )
    .sort()
    .map((prefix: string) => ({ key: prefix, text: prefix, value: prefix }));

    const visibleAlerts = alerts
        .filter((a: any) => !teacherId || (a?.course?.teachers ?? []).some((o: any) => String(o.id) === teacherId))
        .filter((a: any) => !identifierPrefix || a?.user?.identifier?.startsWith(identifierPrefix));

    const sortedAlerts = missedClassesSort
        ? [...visibleAlerts].sort((a: any, b: any) => missedClassesSort === 'ascending'
            ? a.absentCount - b.absentCount
            : b.absentCount - a.absentCount)
        : visibleAlerts;

    return (
        <ResponsiveContainer>
            <Segment style={{ padding: '2em 0em' }} vertical>
                <Container>
                    <Header as='h1' textAlign='center'>Absence Alerts</Header>
                    <Dropdown
                        placeholder='Tags'
                        fluid
                        multiple
                        value={tagIds}
                        onChange={(_, { value }) => {
                            const next = value as string[];
                            setTagIds(next);
                            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
                            refetch({ filters: next.length > 0 ? next.join(',') : '' });
                        }}
                        search
                        selection
                        options={
                            tagsLoading || tagsError ? [] :
                                (tagsData && tagsData.tags || [])
                                    .map((p: any) => ({
                                        key: p.id,
                                        text: p.name,
                                        value: `tagId:${p.id}`,
                                    }))
                        }
                    />
                    <Button
                        color='red'
                        loading={clearing}
                        disabled={clearing || alerts.length === 0}
                        style={{ marginTop: '0.5em' }}
                        onClick={async () => {
                            if (!window.confirm(
                                `Permanently delete ${alerts.length} alert${alerts.length === 1 ? '' : 's'}` +
                                (tagIds.length > 0 ? ' matching the selected tags' : '') +
                                '? This cannot be undone.'
                            )) {
                                return;
                            }
                            await clearAlerts({
                                variables: { filters: tagIds.length > 0 ? tagIds.join(',') : '' }
                            }).then(() => {
                                refetch({ filters: tagIds.length > 0 ? tagIds.join(',') : '' });
                            }).catch((err) => {
                                console.error('Error clearing attendance alerts:', err);
                            });
                        }}
                    >
                        Clear all alerts
                    </Button>
                    <Dropdown
                        placeholder='Teacher'
                        fluid
                        clearable
                        search
                        selection
                        value={teacherId}
                        onChange={(_, { value }) => {
                            const next = (value as string) || '';
                            setTeacherId(next);
                            localStorage.setItem(TEACHER_STORAGE_KEY, JSON.stringify(next));
                        }}
                        options={teacherOptions}
                        style={{ marginTop: '0.5em' }}
                    />
                    <Dropdown
                        placeholder='Identifier Prefix'
                        fluid
                        clearable
                        search
                        selection
                        value={identifierPrefix}
                        onChange={(_, { value }) => {
                            const next = (value as string) || '';
                            setIdentifierPrefix(next);
                            localStorage.setItem(IDENTIFIER_PREFIX_STORAGE_KEY, JSON.stringify(next));
                        }}
                        options={prefixOptions}
                        style={{ marginTop: '0.5em' }}
                    />
                    {/* Table of the alerts */}
                    <Table celled>
                        <Table.Header style={{ position: 'sticky', top: 45, zIndex: 2, backgroundColor: '#f9fafb', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                            <Table.Row>
                                <Table.HeaderCell>Class</Table.HeaderCell>
                                <Table.HeaderCell>Teacher</Table.HeaderCell>
                                <Table.HeaderCell>Person</Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={missedClassesSort ?? undefined}
                                    onClick={() => setMissedClassesSort(prev => prev === 'descending' ? 'ascending' : 'descending')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Missed classes{' '}
                                    <Icon name={missedClassesSort === 'ascending' ? 'sort up' : missedClassesSort === 'descending' ? 'sort down' : 'sort'} />
                                </Table.HeaderCell>
                                <Table.HeaderCell>Warn student</Table.HeaderCell>
                                <Table.HeaderCell>Remove student</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>


                        <Table.Body>
                            {loading ? (<p>Loading...</p>) : (sortedAlerts.length === 0 ? <p>No results</p> : sortedAlerts.map((alert: any) => (
                                <Table.Row key={alert.id}>
                                    <Table.Cell><Link to={`/classes/${alert.course.id}`}>{alert.course.name}</Link></Table.Cell>
                                    <Table.Cell>{alert.course.teachers.map(teacherItem) || '-'}</Table.Cell>
                                    <Table.Cell><Link to={`/people/${alert.user.id}`}>{alert.user.name} ({alert.user.identifier})</Link>
                                        {' | Ph:'}<a href={`tel:${alert.user.phone}`}>{alert.user.phone}</a>
                                    </Table.Cell>
                                    <Table.Cell style={{ backgroundColor: decideBackgroundColor(alert.absentCount) }}>{alert.absentCount}</Table.Cell>
                                    <Table.Cell>
                                        {alert?.caller?.id ? (
                                        <span>Student has been warned by {alert.caller.name} ({moment(Number(alert.calledAt)).format('Do MMM, h a')})</span>
                                        ): (<>
                                            <Button
                                                color = 'green'
                                                onClick = { async() => {
                                                    await setCalled({
                                            variables: {
                                                alertId: alert.id,
                                                        }
                                                    }).then(() => {
                                            refetch({ filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "" }); // Refetch the alerts after kicking
                                                    }).catch((err) => {
                                            console.error('Error setting student as called:', err);
                                                    });
                                                }}
                                            >
                                        Student has been warned
                                    </Button>
                                </>)                                      }
                        </Table.Cell>
                        <Table.Cell>
                            {
                                <Button
                                    color='yellow'
                                    onClick={async () => {
                                        // Kick the user from the class
                                        await kickFromClass({
                                            variables: {
                                                userId: alert.user.id,
                                                courseId: alert.course.id
                                            }
                                        }).then(() => {
                                            refetch({ filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "" }); // Refetch the alerts after kicking
                                        }).catch((err) => {
                                            console.error('Error kicking student:', err);
                                        });
                                    }}
                                >
                                    Remove from class
                                </Button>
                            }
                        </Table.Cell>
                    </Table.Row>
                            )))}
                </Table.Body>
            </Table>

        </Container>
            </Segment >
        </ResponsiveContainer >
    )
}

export { Attendance }
